import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../../context/ThemeContext";
import { brand, brandTextColor } from "../../../config/theme";
import {
  fetchHikvisionDevices,
  firestoreInstantToIso,
  type HikvisionDevice,
  type FirestoreInstant,
} from "../../../store/oasis/hikvisionAccessStore";
import {
  fetchTruckYardOptions,
  type TruckYardOption,
} from "../../../utils/truckYardOptions";
import {
  buildRelayEnv,
  buildBridgeAppsettings,
  buildInstallCommands,
  webhookUrlFromApiUrl,
  type RelayConfig,
  type RelayEventSource,
} from "./configTemplates";
import Swal from "sweetalert2";

// ─── Wizard form ───────────────────────────────────────────────────────────────

/**
 * La URL del webhook del ENTORNO EN EL QUE CORRE esta pantalla.
 *
 * Acá había `import.meta.env.VITE_HIKVISION_WEBHOOK_URL || "<la de development>"`,
 * y ese `||` caía SIEMPRE al fallback: `VITE_HIKVISION_WEBHOOK_URL` aparece una
 * sola vez en todo el repo —en esa misma línea— y no la define ningún `.env` ni
 * ningún workflow. O sea que un operador en app.dumaxst.com generaba un `.env`
 * que apuntaba el relay de un patio de PRODUCCIÓN a development. La pantalla no
 * se veía rota: mentía, que es peor, porque el patio se instala y queda mudo sin
 * que nada falle a la vista.
 *
 * Tampoco alcanzaba con definir la variable en un `.env`: los `.env*` de
 * `apps/v5` no están versionados y `deploy-production.yml:90` sobrescribe el
 * archivo con `echo "VITE_THEME=..." > apps/v5/.env` antes de compilar. Es el
 * mismo patrón que CLAUDE.md ya documenta para las Functions — un valor puesto
 * ahí queda inerte y no falla.
 *
 * Por eso se DERIVA de `VITE_FIREBASE_API_URL`, que sí está definida en cada
 * entorno y ya apunta al mismo proyecto y la misma región: sólo cambia el
 * nombre de la función (`torreDeControlApi` -> `hikvisionWebhook`). Así no
 * existe el estado "entorno nuevo con la variable sin configurar", que es
 * exactamente cómo nació este defecto.
 */
function webhookUrlPorDefecto(): string {
  // Vacío si no se puede derivar, no la de development: el campo es obligatorio
  // (`validate`), así que un vacío obliga a pegar la URL correcta. Un default
  // equivocado se copia. La regla vive en configTemplates (pura y testeada).
  return webhookUrlFromApiUrl(import.meta.env.VITE_FIREBASE_API_URL as string | undefined);
}

const EMPTY_CONFIG: RelayConfig = {
  siteName: "",
  controllerIp: "",
  controllerPort: 8000,
  username: "admin",
  deviceSerial: "",
  webhookUrl: webhookUrlPorDefecto(),
  eventSource: "bridge",
};

function validate(cfg: RelayConfig): Partial<Record<keyof RelayConfig, string>> {
  const errors: Partial<Record<keyof RelayConfig, string>> = {};
  if (!cfg.controllerIp.trim()) errors.controllerIp = "required";
  if (!cfg.webhookUrl.trim()) errors.webhookUrl = "required";
  if (cfg.eventSource === "bridge" && !cfg.deviceSerial.trim()) {
    errors.deviceSerial = "required";
  }
  return errors;
}

// ─── Health grouping ────────────────────────────────────────────────────────────
// A relay serves one physical controller ("console"); doors that share a
// device_serial belong to it and share one heartbeat. We group the flat door
// list by serial to present an agent-centric health view — the same grouping
// the access-control screen uses.

interface RelayGroup {
  serial: string;
  model: string | null;
  ip: string | null;
  yardName: string | null;
  connectionMode: HikvisionDevice["connection_mode"];
  doors: HikvisionDevice[];
  connected: boolean;
  lastHeartbeat: string | null;
  connectionLostAt: string | null;
  lastEventAt: string | null;
}

function isConnected(d: HikvisionDevice): boolean {
  if (d.connection_mode === "relay") return d.connection_status === "connected";
  return d.status === "online";
}

/**
 * Un instante del backend, que NO siempre es un string ISO.
 *
 * `getHikvisionDeviceList` devuelve `doc.data()` crudo, y `last_heartbeat_at`,
 * `last_event_at` y `connection_lost_at` se escriben con `serverTimestamp()`.
 * El Timestamp de `@google-cloud/firestore` no define `toJSON()`, así que por
 * el cable viajan como `{_seconds, _nanoseconds}` — no como ISO, que es lo que
 * decía el tipo. `new Date({_seconds})` es `Invalid Date`, y de ahí salía el
 * `hace NaN d` que se veía en las tres tarjetas de esta pantalla.
 *
 * La conversión se hace en el front y no en la API porque `hikvisionDeviceList`
 * lo consumen dos pantallas: cambiar el contrato arregla una y puede romper la
 * otra. El normalizador vive junto al tipo, en el store, para que la próxima
 * pantalla que lo consuma lo encuentre.
 */
type Instante = FirestoreInstant | undefined;
const aIso = firestoreInstantToIso;

function newer(a: string | null, b: Instante): string | null {
  const bIso = aIso(b);
  if (!bIso) return a;
  if (!a) return bIso;
  return new Date(bIso) > new Date(a) ? bIso : a;
}

function groupRelays(devices: HikvisionDevice[]): RelayGroup[] {
  const map = new Map<string, RelayGroup>();
  for (const d of devices) {
    let g = map.get(d.device_serial);
    if (!g) {
      g = {
        serial: d.device_serial,
        model: d.controller_model ?? null,
        ip: d.ip_address ?? null,
        yardName: d.yard_name ?? null,
        connectionMode: d.connection_mode,
        doors: [],
        connected: false,
        lastHeartbeat: null,
        connectionLostAt: null,
        lastEventAt: null,
      };
      map.set(d.device_serial, g);
    }
    g.doors.push(d);
    if (isConnected(d)) g.connected = true;
    g.lastHeartbeat = newer(g.lastHeartbeat, d.last_heartbeat_at);
    g.connectionLostAt = newer(g.connectionLostAt, d.connection_lost_at);
    g.lastEventAt = newer(g.lastEventAt, d.last_event_at);
    if (!g.model && d.controller_model) g.model = d.controller_model;
    if (!g.ip && d.ip_address) g.ip = d.ip_address;
  }
  for (const g of map.values()) g.doors.sort((a, b) => a.door_no - b.door_no);
  return Array.from(map.values()).sort((a, b) =>
    (a.model || a.serial).localeCompare(b.model || b.serial),
  );
}

function relativeTime(valor: Instante, t: ReturnType<typeof useTranslation>["t"]): string {
  const iso = aIso(valor);
  if (!iso) return t("relay.health.never");
  const ts = new Date(iso).getTime();
  // Guarda que a esta pantalla le faltaba y que la hermana sí tiene
  // (accessControlSection). Sin ella, cualquier valor que no parsee se propaga
  // como NaN por toda la aritmética y termina pintado como "hace NaN d" —
  // un dato inventado donde debería decirse que no se sabe.
  if (Number.isNaN(ts)) return t("relay.health.never");
  const ms = Date.now() - ts;
  const min = Math.floor(ms / 60000);
  if (min < 1) return t("relay.health.now");
  if (min < 60) return t("relay.health.minutesAgo", { count: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t("relay.health.hoursAgo", { count: h });
  return t("relay.health.daysAgo", { count: Math.floor(h / 24) });
}

// ─── Small reusable bits ─────────────────────────────────────────────────────────

function downloadText(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function CodeBlock({
  title,
  filename,
  content,
  language,
  isDarkMode,
  t,
}: {
  title: string;
  filename: string;
  content: string;
  language: string;
  isDarkMode: boolean;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — the download button still works */
    }
  };
  return (
    <div
      className={`rounded-xl border overflow-hidden ${
        isDarkMode ? "border-dark-border bg-[#1e1e1e]" : "border-gray-200 bg-gray-50"
      }`}
    >
      <div
        className={`flex items-center justify-between px-4 py-2.5 border-b ${
          isDarkMode ? "border-dark-border" : "border-gray-200"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <i className="ri-file-code-line text-primary-500 shrink-0" />
          <span
            className={`text-sm font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {title}
          </span>
          <code
            className={`text-xs font-mono px-1.5 py-0.5 rounded ${
              isDarkMode ? "bg-black/30 text-gray-400" : "bg-gray-200 text-gray-600"
            }`}
          >
            {filename}
          </code>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={copy}
            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg transition-colors ${
              isDarkMode
                ? "text-gray-300 hover:bg-white/10"
                : "text-gray-600 hover:bg-gray-200"
            }`}
          >
            <i className={copied ? "ri-check-line text-green-500" : "ri-file-copy-line"} />
            {copied ? t("relay.wizard.copied") : t("relay.wizard.copy")}
          </button>
          <button
            onClick={() => downloadText(filename, content)}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: brand.primary }}
          >
            <i className="ri-download-2-line" />
            {t("relay.wizard.download")}
          </button>
        </div>
      </div>
      <pre className="p-4 overflow-x-auto text-xs leading-relaxed">
        <code
          className={`font-mono ${language} ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
        >
          {content}
        </code>
      </pre>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────────

type Tab = "wizard" | "health";

export default function RelaySection() {
  const { t } = useTranslation("truckYard");
  const { isDarkMode } = useDarkMode();
  // Arranca en "health": lo que se viene a ver acá casi siempre es si los
  // patios están vivos, no a generar un `.env`. Además el estado sólo se carga
  // al ENTRAR a esa pestaña (más abajo), así que con "wizard" por defecto lo
  // primero que veía un operador era un formulario vacío y ningún dato.
  const [tab, setTab] = useState<Tab>("health");

  const cardBg = isDarkMode ? "bg-dark-card" : "bg-white";
  const cardBorder = isDarkMode ? "border-dark-border" : "border-gray-200";
  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-600";
  const inputCls = `w-full px-3 py-2.5 ${
    isDarkMode ? "bg-[#252525]" : "bg-gray-50"
  } border border-transparent rounded-xl ${textPrimary} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm`;
  const labelCls = `text-xs font-medium ${textSecondary} mb-1.5 block`;

  // Wizard state
  const [cfg, setCfg] = useState<RelayConfig>(EMPTY_CONFIG);
  const [errors, setErrors] = useState<Partial<Record<keyof RelayConfig, string>>>({});
  const [generated, setGenerated] = useState(false);
  const setField = <K extends keyof RelayConfig>(key: K, value: RelayConfig[K]) => {
    setCfg((prev) => ({ ...prev, [key]: value }));
    setGenerated(false);
  };

  // Yard options (convenience: pick a yard to prefill the site name)
  const [yards, setYards] = useState<TruckYardOption[]>([]);
  useEffect(() => {
    fetchTruckYardOptions()
      .then(setYards)
      .catch(() => setYards([]));
  }, []);

  const handleGenerate = () => {
    const errs = validate(cfg);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      Swal.fire({
        icon: "warning",
        title: t("relay.wizard.missingTitle"),
        text: t("relay.wizard.missingText"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    setGenerated(true);
  };

  const relayEnv = useMemo(() => buildRelayEnv(cfg), [cfg]);
  const bridgeSettings = useMemo(() => buildBridgeAppsettings(cfg), [cfg]);
  const installCmds = useMemo(() => buildInstallCommands(cfg), [cfg]);

  // Health state
  const [devices, setDevices] = useState<HikvisionDevice[]>([]);
  const [healthLoading, setHealthLoading] = useState(false);
  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const res = await fetchHikvisionDevices({ limit: 100 });
      setDevices(res.data);
    } catch (err) {
      console.error("Error loading relay health:", err);
    } finally {
      setHealthLoading(false);
    }
  }, []);
  useEffect(() => {
    if (tab === "health") loadHealth();
  }, [tab, loadHealth]);

  const relayGroups = useMemo(() => groupRelays(devices), [devices]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${brand.primary}1a`, color: brandTextColor }}
          >
            <i className="ri-router-line text-xl" />
          </div>
          <div className="min-w-0">
            <h1 className={`text-lg font-semibold ${textPrimary}`}>
              {t("relay.title")}
            </h1>
            <p className={`text-sm ${textSecondary}`}>{t("relay.subtitle")}</p>
          </div>
        </div>

        {/* Tabs */}
        <div
          className={`inline-flex p-1 rounded-xl border ${cardBorder} ${
            isDarkMode ? "bg-dark-card" : "bg-gray-100"
          }`}
        >
          {(["wizard", "health"] as Tab[]).map((tabId) => {
            const active = tab === tabId;
            return (
              <button
                key={tabId}
                onClick={() => setTab(tabId)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                  active
                    ? "text-white"
                    : `${textSecondary} hover:${textPrimary}`
                }`}
                style={active ? { backgroundColor: brand.primary } : undefined}
              >
                <i className={tabId === "wizard" ? "ri-tools-line" : "ri-pulse-line"} />
                {t(`relay.tabs.${tabId}`)}
              </button>
            );
          })}
        </div>

        {/* ─── Tab: Wizard ─────────────────────────────────────────────── */}
        {tab === "wizard" && (
          <div className="space-y-6">
            <div className={`rounded-2xl border ${cardBorder} ${cardBg} p-5 space-y-5`}>
              <div className="flex items-center gap-2">
                <i className="ri-settings-3-line text-primary-500" />
                <h2 className={`text-sm font-semibold ${textPrimary}`}>
                  {t("relay.wizard.sectionTitle")}
                </h2>
              </div>

              {/* Event source toggle */}
              <div>
                <label className={labelCls}>{t("relay.wizard.eventSource")}</label>
                <div className={`inline-flex p-1 rounded-xl ${isDarkMode ? "bg-[#252525]" : "bg-gray-100"}`}>
                  {(["bridge", "direct"] as RelayEventSource[]).map((src) => {
                    const active = cfg.eventSource === src;
                    return (
                      <button
                        key={src}
                        type="button"
                        onClick={() => setField("eventSource", src)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          active ? "text-white" : textSecondary
                        }`}
                        style={active ? { backgroundColor: brand.primary } : undefined}
                      >
                        {t(`relay.wizard.source.${src}`)}
                      </button>
                    );
                  })}
                </div>
                <p className={`text-xs mt-1.5 ${textSecondary}`}>
                  {t(`relay.wizard.sourceHint.${cfg.eventSource}`)}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Site / yard */}
                <div>
                  <label className={labelCls}>{t("relay.wizard.site")}</label>
                  <input
                    className={inputCls}
                    list="relay-yard-list"
                    value={cfg.siteName}
                    onChange={(e) => setField("siteName", e.target.value)}
                    placeholder={t("relay.wizard.sitePlaceholder")}
                  />
                  <datalist id="relay-yard-list">
                    {yards.map((y) => (
                      <option key={y.id} value={y.name} />
                    ))}
                  </datalist>
                </div>

                {/* Controller IP */}
                <div>
                  <label className={labelCls}>
                    {t("relay.wizard.controllerIp")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    className={`${inputCls} font-mono ${errors.controllerIp ? "!border-red-500/60" : ""}`}
                    value={cfg.controllerIp}
                    onChange={(e) => setField("controllerIp", e.target.value)}
                    placeholder="192.168.1.239"
                  />
                </div>

                {/* Controller port (bridge only) */}
                {cfg.eventSource === "bridge" && (
                  <div>
                    <label className={labelCls}>{t("relay.wizard.controllerPort")}</label>
                    <input
                      type="number"
                      className={`${inputCls} font-mono`}
                      value={cfg.controllerPort}
                      onChange={(e) => setField("controllerPort", Number(e.target.value) || 8000)}
                      placeholder="8000"
                    />
                  </div>
                )}

                {/* Username */}
                <div>
                  <label className={labelCls}>{t("relay.wizard.username")}</label>
                  <input
                    className={inputCls}
                    value={cfg.username}
                    onChange={(e) => setField("username", e.target.value)}
                    placeholder="admin"
                  />
                </div>

                {/* Device serial (bridge only) */}
                {cfg.eventSource === "bridge" && (
                  <div>
                    <label className={labelCls}>
                      {t("relay.wizard.deviceSerial")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={`${inputCls} font-mono ${errors.deviceSerial ? "!border-red-500/60" : ""}`}
                      value={cfg.deviceSerial}
                      onChange={(e) => setField("deviceSerial", e.target.value)}
                      // El serial LARGO, que es el que hay que registrar. Acá
                      // decía "FY4453143" — el corto — y con el corto no se
                      // ingesta un solo pase (CLAUDE.md, docs/SETUP_PC_RELAY.md).
                      // Un placeholder es una instrucción: el que mostraba era
                      // exactamente la forma que rompe el patio.
                      placeholder="DS-K281220250624V010002ENFY4453143"
                    />
                  </div>
                )}

                {/* Webhook URL */}
                <div className="sm:col-span-2">
                  <label className={labelCls}>
                    {t("relay.wizard.webhookUrl")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    className={`${inputCls} font-mono text-xs ${errors.webhookUrl ? "!border-red-500/60" : ""}`}
                    value={cfg.webhookUrl}
                    // El campo se autocompleta solo, pero si la derivación no
                    // pudo resolver queda vacío y es obligatorio. Sin la forma a
                    // la vista, el operador tiene que ir a buscarla a otro lado.
                    placeholder="https://us-central1-<proyecto>.cloudfunctions.net/hikvisionWebhook"
                    onChange={(e) => setField("webhookUrl", e.target.value)}
                  />
                </div>
              </div>

              {/* Secrets note */}
              <div
                className={`flex items-start gap-2 text-xs rounded-xl p-3 ${
                  isDarkMode ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-700"
                }`}
              >
                <i className="ri-shield-keyhole-line mt-0.5 shrink-0" />
                <span>{t("relay.wizard.secretsNote")}</span>
              </div>

              <button
                onClick={handleGenerate}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-medium transition-opacity hover:opacity-90"
                style={{ backgroundColor: brand.primary }}
              >
                <i className="ri-magic-line" />
                {t("relay.wizard.generate")}
              </button>
            </div>

            {/* Generated output */}
            {generated && (
              <div className="space-y-4">
                <CodeBlock
                  title={t("relay.wizard.relayEnvTitle")}
                  filename=".env"
                  content={relayEnv}
                  language="language-bash"
                  isDarkMode={isDarkMode}
                  t={t}
                />
                {bridgeSettings && (
                  <CodeBlock
                    title={t("relay.wizard.bridgeSettingsTitle")}
                    filename="appsettings.json"
                    content={bridgeSettings}
                    language="language-json"
                    isDarkMode={isDarkMode}
                    t={t}
                  />
                )}
                <CodeBlock
                  title={t("relay.wizard.installTitle")}
                  filename="install.ps1"
                  content={installCmds}
                  language="language-powershell"
                  isDarkMode={isDarkMode}
                  t={t}
                />
              </div>
            )}
          </div>
        )}

        {/* ─── Tab: Health ─────────────────────────────────────────────── */}
        {tab === "health" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className={`text-sm ${textSecondary}`}>{t("relay.health.subtitle")}</p>
              <button
                onClick={loadHealth}
                className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border ${cardBorder} ${textSecondary} hover:${textPrimary} transition-colors`}
              >
                <i className={`ri-refresh-line ${healthLoading ? "animate-spin" : ""}`} />
                {t("relay.health.refresh")}
              </button>
            </div>

            {healthLoading && relayGroups.length === 0 ? (
              <div className={`text-center py-16 ${textSecondary}`}>
                <i className="ri-loader-4-line animate-spin text-2xl" />
              </div>
            ) : relayGroups.length === 0 ? (
              <div className={`rounded-2xl border ${cardBorder} ${cardBg} text-center py-16`}>
                <i className={`ri-router-line text-3xl ${textSecondary}`} />
                <p className={`mt-2 text-sm ${textSecondary}`}>{t("relay.health.empty")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {relayGroups.map((g) => (
                  <div
                    key={g.serial}
                    className={`rounded-2xl border ${cardBorder} ${cardBg} p-5`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-semibold ${textPrimary}`}>
                            {g.model || g.serial}
                          </h3>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full border ${
                              g.connected
                                ? "bg-green-500/15 text-green-400 border-green-500/30"
                                : "bg-red-500/15 text-red-400 border-red-500/30"
                            }`}
                          >
                            {g.connected
                              ? t("relay.health.online")
                              : t("relay.health.offline")}
                          </span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              isDarkMode ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {t(`relay.health.mode.${g.connectionMode}`)}
                          </span>
                        </div>
                        <p className={`text-xs mt-1 font-mono ${textSecondary}`}>
                          {g.serial}
                          {g.ip ? ` · ${g.ip}` : ""}
                          {g.yardName ? ` · ${g.yardName}` : ""}
                        </p>
                      </div>
                      <i
                        className={`ri-circle-fill text-[10px] mt-1.5 ${
                          g.connected ? "text-green-500" : "text-red-500"
                        }`}
                      />
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                      <Stat
                        label={t("relay.health.doors")}
                        value={String(g.doors.length)}
                        textPrimary={textPrimary}
                        textSecondary={textSecondary}
                      />
                      <Stat
                        label={t("relay.health.lastHeartbeat")}
                        value={relativeTime(g.lastHeartbeat, t)}
                        textPrimary={textPrimary}
                        textSecondary={textSecondary}
                      />
                      <Stat
                        label={t("relay.health.lastEvent")}
                        value={relativeTime(g.lastEventAt, t)}
                        textPrimary={textPrimary}
                        textSecondary={textSecondary}
                      />
                      <Stat
                        label={t("relay.health.lostAt")}
                        value={
                          g.connected || !g.connectionLostAt
                            ? "—"
                            : relativeTime(g.connectionLostAt, t)
                        }
                        textPrimary={textPrimary}
                        textSecondary={textSecondary}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  textPrimary,
  textSecondary,
}: {
  label: string;
  value: string;
  textPrimary: string;
  textSecondary: string;
}) {
  return (
    <div>
      <p className={`text-[11px] uppercase tracking-wide ${textSecondary}`}>{label}</p>
      <p className={`text-sm font-medium mt-0.5 ${textPrimary}`}>{value}</p>
    </div>
  );
}
